import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  videoContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    background: '#000',
  },
  video: {
    display: 'block',
    transform: 'scaleX(-1)', // Mirror effect
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'scaleX(-1)', // Mirror effect
  },
  status: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    fontSize: '0.9rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  statusError: {
    background: 'rgba(255, 82, 82, 0.2)',
    color: '#ff5252',
  },
  statusSuccess: {
    background: 'rgba(0, 230, 118, 0.2)',
    color: '#00e676',
  },
  buttonRow: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  button: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
    color: '#000',
  },
  secondaryButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  tab: {
    padding: '0.5rem 1rem',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  tabActive: {
    background: 'rgba(0, 217, 255, 0.3)',
    color: '#00d9ff',
  },
  imagePreview: {
    maxWidth: '640px',
    maxHeight: '480px',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  },
  fileInput: {
    display: 'none',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTop: '4px solid #00d9ff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframes for spinner
const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function FaceCapture({ onFaceDetected, mode = 'register', autoDetect = false }) {
  const videoRef = useRef();
  const canvasRef = useRef();
  const imageRef = useRef();
  const imageCanvasRef = useRef();
  const fileInputRef = useRef();
  
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [status, setStatus] = useState('Loading face detection models...');
  const [statusType, setStatusType] = useState('info');
  const [captureMode, setCaptureMode] = useState('camera'); // 'camera' or 'photo'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stream, setStream] = useState(null);

  // Load models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        
        setIsModelLoaded(true);
        setStatus('Models loaded. Select camera or upload a photo.');
        setStatusType('success');
      } catch (error) {
        setStatus('Error loading models: ' + error.message);
        setStatusType('error');
      }
    };
    
    loadModels();
    
    // Add spinner animation
    const styleSheet = document.createElement('style');
    styleSheet.textContent = spinnerKeyframes;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Start camera when switching to camera mode
  useEffect(() => {
    if (captureMode === 'camera' && isModelLoaded) {
      startCamera();
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [captureMode, isModelLoaded]);

  const startCamera = async () => {
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      setStream(newStream);
      setStatus('Camera ready. Position your face and click Capture.');
      setStatusType('info');
    } catch (error) {
      setStatus('Camera error: ' + error.message);
      setStatusType('error');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const detectFace = useCallback(async (element, canvas) => {
    setIsProcessing(true);
    setStatus('Detecting face...');
    setStatusType('info');
    
    try {
      const detection = await faceapi
        .detectSingleFace(element)
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        // Draw detection on canvas
        const displaySize = { 
          width: element.videoWidth || element.width, 
          height: element.videoHeight || element.height 
        };
        faceapi.matchDimensions(canvas, displaySize);
        
        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        faceapi.draw.drawDetections(canvas, resizedDetection);
        faceapi.draw.drawFaceLandmarks(canvas, resizedDetection);

        const faceVector = Array.from(detection.descriptor);
        const confidence = detection.detection.score;
        
        setStatus(`Face detected! Confidence: ${(confidence * 100).toFixed(1)}%`);
        setStatusType('success');
        
        onFaceDetected?.({
          vector: faceVector,
          confidence: confidence,
          box: detection.detection.box
        });
      } else {
        setStatus('No face detected. Please try again.');
        setStatusType('error');
      }
    } catch (error) {
      setStatus('Detection error: ' + error.message);
      setStatusType('error');
    }
    
    setIsProcessing(false);
  }, [onFaceDetected]);

  const captureFromCamera = () => {
    if (!isModelLoaded || !videoRef.current) return;
    detectFace(videoRef.current, canvasRef.current);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    stopCamera();
    
    // Clear previous detection
    if (imageCanvasRef.current) {
      const ctx = imageCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, imageCanvasRef.current.width, imageCanvasRef.current.height);
    }
  };

  const handleImageLoad = () => {
    if (!isModelLoaded || !imageRef.current) return;
    
    // Set canvas size to match image
    if (imageCanvasRef.current && imageRef.current) {
      imageCanvasRef.current.width = imageRef.current.width;
      imageCanvasRef.current.height = imageRef.current.height;
    }
    
    detectFace(imageRef.current, imageCanvasRef.current);
  };

  const switchMode = (newMode) => {
    setCaptureMode(newMode);
    setPreviewUrl(null);
    setStatus('Ready');
    setStatusType('info');
    
    // Clear canvases
    [canvasRef, imageCanvasRef].forEach(ref => {
      if (ref.current) {
        const ctx = ref.current.getContext('2d');
        ctx.clearRect(0, 0, ref.current.width, ref.current.height);
      }
    });
  };

  if (!isModelLoaded) {
    return (
      <div style={styles.loader}>
        <div style={styles.spinner} />
        <p>{status}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Mode Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(captureMode === 'camera' ? styles.tabActive : {}) }}
          onClick={() => switchMode('camera')}
        >
          📹 Camera
        </button>
        <button
          style={{ ...styles.tab, ...(captureMode === 'photo' ? styles.tabActive : {}) }}
          onClick={() => switchMode('photo')}
        >
          🖼️ Photo Upload
        </button>
      </div>

      {/* Camera Mode */}
      {captureMode === 'camera' && (
        <>
          <div style={{ ...styles.videoContainer, width: 640, height: 480 }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              width={640}
              height={480}
              style={styles.video}
            />
            <canvas
              ref={canvasRef}
              width={640}
              height={480}
              style={styles.canvas}
            />
          </div>
          
          <div style={styles.buttonRow}>
            <button
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(isProcessing ? styles.disabledButton : {})
              }}
              onClick={captureFromCamera}
              disabled={isProcessing}
            >
              {isProcessing ? '⏳ Processing...' : '📸 Capture Face'}
            </button>
          </div>
        </>
      )}

      {/* Photo Upload Mode */}
      {captureMode === 'photo' && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileSelect}
            style={styles.fileInput}
          />
          
          {previewUrl ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                ref={imageRef}
                src={previewUrl}
                onLoad={handleImageLoad}
                style={styles.imagePreview}
                alt="Uploaded face"
              />
              <canvas
                ref={imageCanvasRef}
                style={{ position: 'absolute', top: 0, left: 0 }}
              />
            </div>
          ) : (
            <div
              style={{
                width: 640,
                height: 300,
                border: '2px dashed rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <span style={{ fontSize: '3rem', marginBottom: '1rem' }}>📷</span>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Click to upload a photo
              </p>
            </div>
          )}
          
          <div style={styles.buttonRow}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => fileInputRef.current?.click()}
            >
              📁 Choose Photo
            </button>
            {previewUrl && (
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  ...(isProcessing ? styles.disabledButton : {})
                }}
                onClick={() => detectFace(imageRef.current, imageCanvasRef.current)}
                disabled={isProcessing}
              >
                {isProcessing ? '⏳ Processing...' : '🔄 Re-detect Face'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Status Message */}
      <div
        style={{
          ...styles.status,
          ...(statusType === 'error' ? styles.statusError : {}),
          ...(statusType === 'success' ? styles.statusSuccess : {}),
        }}
      >
        {status}
      </div>
    </div>
  );
}

export default FaceCapture;
